import { useConfigure } from "react-instantsearch";
import type { PageHit } from "@/features/search/components/Results/models/pageHit";
import React, { useMemo } from "react";
import {
  PageLinkIcon,
  ProfileIcon,
  DollarIcon,
  WalletIcon,
  SettingsIcon,
  HelpIcon,
  StarIcon,
  TermsIcon,
} from "@picklebet/ui";

const pageIconComponents: Record<string, React.FC> = {
  pageLink: PageLinkIcon,
  profile: ProfileIcon,
  dollar: DollarIcon,
  wallet: WalletIcon,
  settings: SettingsIcon,
  help: HelpIcon,
  star: StarIcon,
  terms: TermsIcon,
};
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { usePermissions } from "@/features/auth/hooks/usePermission";
import { useIsApplePayAvailable } from "@/features/wallet/hooks/useApplePay";
import { useIsGooglePayAvailable } from "@/features/wallet/hooks/useIsGooglePayAvailable";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import type { Permissions } from "@/features/auth/models/permissions";
import { Row } from "./Row/Row";
import { ResultsInIndex } from "./ResultsInIndex/ResultsInIndex";
import { Highlight } from "./Highlight/Highlight";
import { useNormalisedHits } from "@/features/search/components/Results/hooks/useNormalisedHits";

const getPagesFilters = (
  indexName: string,
  loggedIn: boolean,
  country: string,
  isApplePayAvailable: boolean,
  isGooglePayAvailable: boolean,
  permissions: Partial<Permissions>,
) => {
  const filters = [];

  if (indexName === "pages") {
    const countryFilter = country ? ` OR country:${country}` : "";
    if (!loggedIn) {
      filters.push(`country:false AND requiresSession:false`);
    } else if (!isApplePayAvailable && !isGooglePayAvailable) {
      filters.push(
        `(country:false${countryFilter}) AND requiresApplePay:false AND requiresGooglePay:false`,
      );
    } else if (!isApplePayAvailable) {
      filters.push(
        `(country:false${countryFilter}) AND requiresApplePay:false`,
      );
    } else if (!isGooglePayAvailable) {
      filters.push(
        `(country:false${countryFilter}) AND requiresGooglePay:false`,
      );
    } else {
      filters.push(`(country:false${countryFilter})`);
    }
  }

  // figure out required filters
  if (permissions?.viewLiveMarkets !== "GRANTED" && indexName === "matches") {
    // Prematch-origin live matches stay findable - they're viewable, just not bettable
    filters.push(`(bettingType:"prematch" OR prematchMarket:true)`);
  }

  if (
    indexName === "matches" ||
    indexName === "outrights" ||
    indexName === "sports"
  ) {
    if (
      permissions.viewEsportsMarkets === "GRANTED" &&
      permissions.viewSportsMarkets !== "GRANTED"
    ) {
      filters.push("hub:esports");
    }

    if (
      permissions.viewEsportsMarkets !== "GRANTED" &&
      permissions.viewSportsMarkets === "GRANTED"
    ) {
      filters.push("hub:sports");
    }
  }

  return filters.join(" AND ");
};

export const PageResults = () => {
  const { items, results } = useNormalisedHits<PageHit>();

  const isLoggedIn = useIsLoggedIn();
  const permissions = usePermissions();
  const isApplePayAvailable = useIsApplePayAvailable();
  const isGooglePayAvailable = useIsGooglePayAvailable();
  const { profile } = useProfile();

  const indexName = "pages";

  const filters = useMemo(() => {
    return getPagesFilters(
      indexName,
      isLoggedIn,
      profile?.country || "",
      isApplePayAvailable,
      isGooglePayAvailable,
      permissions,
    );
  }, [
    indexName,
    isLoggedIn,
    profile?.country,
    isApplePayAvailable,
    isGooglePayAvailable,
    permissions,
  ]);

  useConfigure({
    filters,
  });

  return (
    <ResultsInIndex
      indexName={`Pages`}
      total={results?.nbHits || 0}
      pages={results?.nbPages || 0}
      perPage={results?.hitsPerPage || 0}
    >
      {(items || []).map((item) => {
        const titleHighlight = item.normalisedHighlights?.title?.[0];
        const IconComponent = pageIconComponents[item.icon] || PageLinkIcon;
        return (
          <Row
            key={item.objectID}
            icon={<IconComponent />}
            title={
              <Highlight
                text={item.title}
                matchedWords={titleHighlight?.matchedWords || []}
                html={titleHighlight?.value || ""}
              />
            }
            to={item.url}
          />
        );
      })}
    </ResultsInIndex>
  );
};
